import React from 'react';
import { Flex, Gutter, Title, BodyText, Div, Icon, GridBag } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { Video, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { getInitialVideo } from '../../../../utils/products';
import { removeEntityByIndex, updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

interface VideosProps {
  title: string;
  videos: Video[] | undefined;
  locale: LocaleResponse;
  handleVideosChange: (videos: Video[]) => void;
  videosError: Errors['videos'];
}

function Videos(props: VideosProps) {
  const { title, videos, locale, handleVideosChange, videosError } = props;
  const { t } = useTranslation('productscreen');

  const handleVideoAdd = () => {
    const video = getInitialVideo(locale.id);
    handleVideosChange([...(videos || []), video]);
  };

  const handleVideoRemoval = (index: number) => {
    if (videos) {
      const updatedVideos = removeEntityByIndex(videos, index);
      handleVideosChange(updatedVideos);
    }
  };

  const handleVideoUrlChange = (index: number, url: string) => {
    if (videos) {
      const updatedVideo = { ...videos[index], url };
      const updatedVideos = updateEntityByIndex(videos, index, updatedVideo);
      handleVideosChange(updatedVideos);
    }
  };

  /* Store the original index of each video. The videos array could f.ex. contain 4 videos, 1 finnish, 1 swedish, 2 norwegian.
  The videos for single locale should be shown at once (e.g. finnish) but those videos store their index in the original array. */
  const videosWithIndex = (videos || [])
    .map((video, index) => ({ ...video, index }))
    .filter(videoWithIndex => videoWithIndex.locale_id === locale.id);

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('videos_section.info')}</BodyText>
      <Gutter />
      {videosError && (
        <BodyText size="sm" color="red600">
          {videosError}
        </BodyText>
      )}
      {videosWithIndex.map((videoWithIndex, filteredVideosIndex) => (
        <Div key={videoWithIndex.index}>
          <GridBag>
            <GridBag.Item md={7}>
              <Flex backgroundHoverColor="gray200" backgroundColor="gray100" padding={{ size: 'xs' }}>
                <Div flexItem={{ sizeMultiplier: 1, alignSelfTo: 'bottom' }} margin={{ right: 'sm' }}>
                  <EditableItem.Input
                    value={videoWithIndex.url}
                    edit={true}
                    inputProps={{
                      name: `video-${videoWithIndex.index}`,
                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleVideoUrlChange(videoWithIndex.index, e.target.value),
                      placeholder: t('videos_section.video_placeholder', { language: locale.name.split(' ')[0] }),
                      label: t('videos_section.video_label', { language: locale.name.split(' ')[0], index: filteredVideosIndex + 1 }),
                      onKeyDown: () => undefined,
                    }}
                  />
                </Div>
                <Div flexItem={{ sizeMultiplier: 0.1, alignSelfTo: 'bottom' }}>
                  <Icon
                    data-testid={`video-${videoWithIndex.index}-delete`}
                    title={t('videos_section.delete_video')}
                    margin={{ bottom: 'xs' }}
                    onClick={() => handleVideoRemoval(videoWithIndex.index)}
                    clickableAppearance={true}
                    size="md"
                    color="gray400"
                    name="delete"
                  />
                </Div>
              </Flex>
            </GridBag.Item>
          </GridBag>
          <Gutter size="xs" />
        </Div>
      ))}
      <Gutter size="sm" />
      <BodyText clickableAppearance={true} color="purple500" onClick={() => handleVideoAdd()}>
        {`+ ${t('videos_section.add_video')}`}
      </BodyText>
    </Flex>
  );
}

export default React.memo(Videos);
